/**
 * TranslateService — Client-side translation with session caching,
 * smart batching, and RPM throttle guard.
 *
 * - translateText()      → single field (uses cache or /api/system/translate)
 * - translateBatchJson()  → batch of keyed fields in ONE Gemini request
 * - translateBatch()      → translates array of texts (uses batch endpoint)
 * - isAiCoolingDown()     → true when frontend cooldown active
 * - getCooldownRemaining()→ seconds remaining
 * - checkAiEnabled()      → server-side gate check (platform + org)
 */

const cache = new Map<string, string>();

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';

// ── RPM Throttle (10 requests / 60 seconds) ────────────────────────────────
const RPM_LIMIT = 10;
const RPM_WINDOW = 60_000; // 60s
const requestTimestamps: number[] = [];
let cooldownEnd = 0;

function recordRequest() {
  const now = Date.now();
  requestTimestamps.push(now);
  // Prune old entries
  while (requestTimestamps.length > 0 && requestTimestamps[0] < now - RPM_WINDOW) {
    requestTimestamps.shift();
  }
  if (requestTimestamps.length >= RPM_LIMIT) {
    cooldownEnd = requestTimestamps[0] + RPM_WINDOW;
  }
}

export function isAiCoolingDown(): boolean {
  return Date.now() < cooldownEnd;
}

export function getCooldownRemaining(): number {
  const diff = cooldownEnd - Date.now();
  return diff > 0 ? Math.ceil(diff / 1000) : 0;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('dm_jwt') ?? '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ── AI Feature Gate Check ───────────────────────────────────────────────────

let aiEnabledCache: { value: boolean; ts: number } | null = null;

export async function checkAiEnabled(): Promise<boolean> {
  // Cache for 30 seconds to avoid hammering
  if (aiEnabledCache && Date.now() - aiEnabledCache.ts < 30_000) return aiEnabledCache.value;
  try {
    const res = await fetch(`${API_BASE}/api/system/ai-enabled`, { headers: getAuthHeaders() });
    if (!res.ok) { aiEnabledCache = { value: false, ts: Date.now() }; return false; }
    const data = await res.json() as { enabled: boolean };
    aiEnabledCache = { value: !!data.enabled, ts: Date.now() };
    return aiEnabledCache.value;
  } catch {
    return false;
  }
}

/** Invalidate gate cache (e.g. after toggling settings) */
export function invalidateAiGateCache() {
  aiEnabledCache = null;
}

// ── Single text translation ─────────────────────────────────────────────────

export async function translateText(
  text: string,
  targetLang = 'ar',
  retry = false,
): Promise<string> {
  const trimmed = text.trim();
  if (!trimmed) return '';

  // Check cache (skip on retry so we get a fresh result)
  const cacheKey = `${targetLang}:${trimmed.toLowerCase()}`;
  if (!retry) {
    const cached = cache.get(cacheKey);
    if (cached) return cached;
  }

  // Throttle check
  if (isAiCoolingDown()) throw new Error('AI_COOLDOWN');

  const res = await fetch(`${API_BASE}/api/system/translate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ text: trimmed, target: targetLang, ...(retry ? { retry: true } : {}) }),
  });

  recordRequest();

  if (res.status === 503 || res.status === 429) {
    const data = await res.json() as { error?: string };
    throw new Error(data.error || 'AI quota exceeded');
  }
  if (res.status === 403) {
    const data = await res.json() as { error?: string };
    throw new Error(data.error || 'AI not enabled');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error || `Translation failed (${res.status})`);
  }

  const data = await res.json() as { translated?: string; error?: string };
  if (!data.translated) throw new Error(data.error || 'No translation returned');

  cache.set(cacheKey, data.translated);
  return data.translated;
}

// ── Batch JSON translation (one Gemini call) ────────────────────────────────

export async function translateBatchJson(
  fields: Record<string, string>,
  targetLang = 'ar',
): Promise<Record<string, string>> {
  // Filter out empty and already-cached
  const toTranslate: Record<string, string> = {};
  const results: Record<string, string> = {};

  for (const [key, value] of Object.entries(fields)) {
    const trimmed = value.trim();
    if (!trimmed) { results[key] = ''; continue; }
    const cacheKey = `${targetLang}:${trimmed.toLowerCase()}`;
    const cached = cache.get(cacheKey);
    if (cached) { results[key] = cached; continue; }
    toTranslate[key] = trimmed;
  }

  if (Object.keys(toTranslate).length === 0) return results;

  if (isAiCoolingDown()) throw new Error('AI_COOLDOWN');

  const res = await fetch(`${API_BASE}/api/system/translate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ batch: toTranslate, target: targetLang }),
  });

  recordRequest();

  if (res.status === 503 || res.status === 429) {
    const data = await res.json() as { error?: string };
    throw new Error(data.error || 'AI quota exceeded');
  }
  if (res.status === 403) {
    const data = await res.json() as { error?: string };
    throw new Error(data.error || 'AI not enabled');
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error || `Batch translation failed (${res.status})`);
  }

  const data = await res.json() as { batch?: Record<string, string>; error?: string };
  if (!data.batch) throw new Error(data.error || 'No batch result');

  // Store in cache
  for (const [key, translated] of Object.entries(data.batch)) {
    const original = toTranslate[key];
    if (original && translated) {
      cache.set(`${targetLang}:${original.toLowerCase()}`, translated);
    }
    results[key] = translated ?? '';
  }

  return results;
}

// ── Legacy parallel batch (still works, falls back to single calls) ─────────

export async function translateBatch(
  texts: string[],
  targetLang = 'ar',
): Promise<string[]> {
  // Build keyed map for batch JSON
  const keyedInput: Record<string, string> = {};
  texts.forEach((t, i) => { if (t.trim()) keyedInput[`f${i}`] = t; });

  if (Object.keys(keyedInput).length === 0) return texts.map(() => '');

  const batchResult = await translateBatchJson(keyedInput, targetLang);
  return texts.map((_, i) => batchResult[`f${i}`] ?? '');
}
